import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Couverture, CouverturesService, TypeCouverture } from 'src/app/core/services/entities/couvertures.service';

export interface ChiffreCarte { libelle: string; valeur: string | number | null | undefined; }
export type TonStatut = 'ok' | 'attention' | 'alerte' | 'neutre';

/** Visuel doux par type quand l'élément n'a pas encore de photo */
const VISUELS: Record<TypeCouverture, { icone: string; fond: string; couleur: string }> = {
  BASSIN: { icone: 'water', fond: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', couleur: '#1d4ed8' },
  PISCICULTURE: { icone: 'set_meal', fond: 'linear-gradient(135deg,#dbeafe,#c7d2fe)', couleur: '#1e40af' },
  TROUPEAU: { icone: 'pets', fond: 'linear-gradient(135deg,#fde4d8,#fdcdbc)', couleur: '#795548' },
  ANIMAL: { icone: 'cruelty_free', fond: 'linear-gradient(135deg,#fde4d8,#f5d0c0)', couleur: '#6d4c41' },
  LOT_VOLAILLE: { icone: 'egg', fond: 'linear-gradient(135deg,#fef3c7,#fde68a)', couleur: '#92400e' },
  CULTURE: { icone: 'eco', fond: 'linear-gradient(135deg,#dcfce7,#a3f69c)', couleur: '#0d631b' },
  ARTICLE_STOCK: { icone: 'inventory_2', fond: 'linear-gradient(135deg,#f3f4f6,#e5e7eb)', couleur: '#4b5563' },
  EQUIPEMENT: { icone: 'construction', fond: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)', couleur: '#3730a3' },
};

/**
 * Carte d'un élément de la ferme (bassin, troupeau, animal, lot, culture) : photo de couverture 4:3 ou
 * visuel par type, pastille de statut et nombre de photos sur l'image, titre, 1 à 3 chiffres clés.
 * Actions (menu) : contenu projeté avec l'attribut bayCarteActions.
 */
@Component({
  selector: 'bay-carte-photo',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article class="bay-carte" [class.bay-carte-active]="actif" [attr.role]="lien ? 'link' : cliquable ? 'button' : null" [attr.tabindex]="actif ? 0 : null"
             [attr.aria-label]="titre" (click)="ouvrir()" (keydown.enter)="ouvrir()">
      <div class="bay-carte-media" [style.background]="visuel.fond">
        <img *ngIf="!imageEnErreur && (src$ | async) as src; else sansPhoto" [src]="src" alt="" loading="lazy" (error)="imageEnErreur = true">
        <ng-template #sansPhoto>
          <span class="material-symbols-outlined bay-carte-icone" [style.color]="visuel.couleur" aria-hidden="true">{{ icone || visuel.icone }}</span>
        </ng-template>
        <span *ngIf="statut" class="bay-carte-statut" [ngClass]="'bay-carte-' + (ton || 'neutre')">{{ statut }}</span>
        <span *ngIf="(couverture?.nombre || 0) > 0" class="bay-carte-nb" [attr.aria-label]="couverture!.nombre + ' photo(s)'">
          <span class="material-symbols-outlined" aria-hidden="true">photo_camera</span>{{ couverture!.nombre }}
        </span>
        <div class="bay-carte-actions" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()">
          <ng-content select="[bayCarteActions]"></ng-content>
        </div>
      </div>
      <div class="bay-carte-corps">
        <h3 class="bay-carte-titre">{{ titre }}</h3>
        <p *ngIf="sousTitre" class="bay-carte-sous">{{ sousTitre }}</p>
        <div *ngIf="chiffres?.length" class="bay-carte-chiffres">
          <div *ngFor="let c of chiffres">
            <span class="bay-carte-valeur">{{ c.valeur ?? '—' }}</span>
            <span class="bay-carte-libelle">{{ c.libelle }}</span>
          </div>
        </div>
        <ng-content></ng-content>
      </div>
    </article>
  `,
  styles: [`
    :host { display:block; height:100%; }
    .bay-carte { height:100%; display:flex; flex-direction:column; background:#fff; border-radius:20px; overflow:hidden;
      box-shadow:0 1px 3px rgba(16,24,40,.08); transition:transform .15s, box-shadow .15s; }
    .bay-carte-active { cursor:pointer; }
    .bay-carte-active:hover, .bay-carte-active:focus-visible { transform:translateY(-3px); box-shadow:0 8px 24px rgba(16,24,40,.12); outline:none; }
    .bay-carte-active:focus-visible { box-shadow:0 0 0 3px #0d631b55; }
    .bay-carte-media { position:relative; aspect-ratio:4/3; display:flex; align-items:center; justify-content:center; }
    .bay-carte-media img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
    .bay-carte-icone { font-size:64px; font-variation-settings:'FILL' 1; opacity:.55; }
    .bay-carte-statut { position:absolute; left:12px; top:12px; padding:4px 10px; border-radius:999px; font-size:12px; font-weight:700;
      background:rgba(255,255,255,.92); color:#374151; backdrop-filter:blur(4px); }
    .bay-carte-ok { color:#0d631b; } .bay-carte-attention { color:#92400e; } .bay-carte-alerte { color:#b91c1c; }
    .bay-carte-nb { position:absolute; left:12px; bottom:12px; display:inline-flex; align-items:center; gap:4px; padding:3px 9px;
      border-radius:999px; font-size:12px; font-weight:600; background:rgba(17,24,39,.6); color:#fff; }
    .bay-carte-nb .material-symbols-outlined { font-size:15px; }
    .bay-carte-actions { position:absolute; right:10px; top:10px; }
    .bay-carte-corps { padding:14px 16px 16px; display:flex; flex-direction:column; gap:4px; flex:1; }
    .bay-carte-titre { font-size:17px; font-weight:700; margin:0; color:#111827; letter-spacing:-.2px; }
    .bay-carte-sous { font-size:13px; color:#4b5563; margin:0; }
    .bay-carte-chiffres { display:flex; gap:20px; margin-top:auto; padding-top:10px; }
    .bay-carte-chiffres > div { display:flex; flex-direction:column; }
    .bay-carte-valeur { font-size:18px; font-weight:700; color:#111827; }
    .bay-carte-libelle { font-size:11px; color:#4b5563; text-transform:uppercase; letter-spacing:.4px; }
  `]
})
export class CartePhotoComponent implements OnChanges {
  @Input() type: TypeCouverture = 'BASSIN';
  @Input() titre = '';
  @Input() sousTitre?: string | null;
  @Input() statut?: string | null;
  @Input() ton?: TonStatut;
  @Input() chiffres?: ChiffreCarte[];
  @Input() couverture?: Couverture | null;
  @Input() lien?: any[] | string | null;
  /** Icône du visuel sans photo, à la place de celle du type (ex. catégorie d'un article) */
  @Input() icone?: string | null;
  /** Carte cliquable sans lien : émet (choisir), ex. ouvrir un panneau sur la même page */
  @Input() cliquable = false;
  @Output() choisir = new EventEmitter<void>();

  src$: Observable<SafeUrl | null> = of(null);
  /** Vignette illisible : on garde le visuel par type */
  imageEnErreur = false;

  constructor(private couvertures: CouverturesService, private router: Router, private sanitizer: DomSanitizer) {}

  get actif(): boolean { return !!this.lien || this.cliquable; }

  get visuel() { return VISUELS[this.type] || VISUELS.BASSIN; }

  private idPhoto?: number;

  ngOnChanges(): void {
    // Les chiffres changent à chaque rendu : l'image n'est rechargée que si la photo change
    const id = this.couverture?.photos?.[0];
    if (id === this.idPhoto) { return; }
    this.idPhoto = id;
    this.imageEnErreur = false;
    // Adresse blob: créée par l'application elle-même (sinon Angular la neutralise en « unsafe: »)
    this.src$ = id
      ? this.couvertures.vignette(id).pipe(map(u => u ? this.sanitizer.bypassSecurityTrustUrl(u) : null))
      : of(null);
  }

  ouvrir(): void {
    if (!this.lien) { if (this.cliquable) { this.choisir.emit(); } return; }
    Array.isArray(this.lien) ? this.router.navigate(this.lien) : this.router.navigateByUrl(this.lien);
  }
}
